import type { CareerLayerApiResponse } from '@/domains/careerLayer/careerLayer.entity';

/**
 * UseCase para filtrar datos de CareerLayer por el año actual
 *
 * Responsabilidades:
 * - Recibir data completa de la API
 * - Filtrar solo el año actual (excluye pasados y futuros)
 * - Retornar estructura filtrada manteniendo formato original
 *
 * Ejemplo:
 * Input: { data: { "2026": {...}, "2025": {...}, "2024": {...} } }
 * Output (año actual 2026): { data: { "2026": {...} } }
 */
export class FilterCareerLayerByYearUseCase {
  /**
   * Ejecuta el filtrado de años
   * @param apiData - Respuesta completa de la API
   * @param currentYear - Año actual (opcional, por defecto usa new Date())
   * @returns Data filtrada con solo el año actual
   */
  execute(
    apiData: CareerLayerApiResponse | null,
    currentYear?: number,
  ): CareerLayerApiResponse {
    if (!apiData?.data) {
      return { data: {} };
    }

    const yearToFilter = currentYear ?? new Date().getFullYear();
    const filteredYears: Record<string, (typeof apiData.data)[string]> = {};

    // Filtrar solo el año actual
    for (const year of Object.keys(apiData.data)) {
      const yearNum = Number.parseInt(year, 10);

      if (!Number.isNaN(yearNum) && yearNum === yearToFilter) {
        filteredYears[year] = apiData.data[year];
      }
    }

    return { data: filteredYears };
  }
}
